// Tournament Tree rail — persistent Competition → Season → Stage → Group → Gameday
// navigation so the operator never feels lost. Click any node to jump to it.
//
// Data note: only the *selected* competition's seasons (from competitionDetail) and
// the *selected* season's stages (from the stages query) are loaded, so deeper
// levels expand under the active path. Other competitions show as collapsed roots
// that load their seasons once selected.

import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import TOKENS from './tokens'
import { Icon, IconBtn } from './Primitives'

const useStyles = makeStyles({
  rail: {
    width: 268,
    flexShrink: 0,
    background: TOKENS.surface,
    borderRight: `1px solid ${TOKENS.border}`,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: TOKENS.font,
  },
  head: {
    height: 56,
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${TOKENS.border}`,
    flexShrink: 0,
  },
  body: { flex: 1, overflowY: 'auto', padding: 8 },
  node: {
    display: 'flex',
    alignItems: 'center',
    height: 28,
    paddingRight: 8,
    borderRadius: TOKENS.radiusSm,
    cursor: 'pointer',
    fontSize: 12.5,
    color: TOKENS.text2,
    transition: 'background .12s, color .12s',
    border: 'none',
    background: 'transparent',
    width: '100%',
    textAlign: 'left',
    '&:hover': { background: TOKENS.surface2 },
  },
  active: {
    background: TOKENS.coralSoft,
    color: TOKENS.coralDark,
    fontWeight: 600,
    '&:hover': { background: TOKENS.coralSoft },
  },
  twisty: {
    width: 16,
    height: 16,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: TOKENS.text3,
    flexShrink: 0,
    marginRight: 4,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    padding: 0,
  },
  label: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  suffix: { fontSize: 10, color: TOKENS.text3, marginLeft: 8, fontFamily: TOKENS.mono },
  badge: {
    marginLeft: 6,
    fontSize: 10,
    padding: '0 6px',
    height: 16,
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    background: TOKENS.surface2,
    color: TOKENS.text3,
    fontWeight: 600,
  },
  newBtn: {
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 32,
    borderRadius: TOKENS.radiusSm,
    border: `1px dashed ${TOKENS.borderStrong}`,
    background: 'transparent',
    color: TOKENS.text3,
    fontSize: 12,
    fontWeight: 500,
    fontFamily: TOKENS.font,
    cursor: 'pointer',
    transition: 'color .15s, border-color .15s',
    '&:hover': { color: TOKENS.coral, borderColor: TOKENS.coral },
  },
})

const TreeNode = ({ level, icon, label, active, hasChildren, expanded, onToggle, onSelect, suffix, badge, dotColor }) => {
  const c = useStyles()
  return (
    <button type="button" className={`${c.node} ${active ? c.active : ''}`} style={{ paddingLeft: 8 + level * 14 }} onClick={onSelect}>
      {hasChildren ? (
        <span
          className={c.twisty}
          onClick={(e) => {
            e.stopPropagation()
            onToggle()
          }}
        >
          <Icon name={expanded ? 'chevronDown' : 'chevronRight'} size={11} stroke={2.5} />
        </span>
      ) : (
        <span className={c.twisty}>
          {dotColor && <span style={{ width: 6, height: 6, borderRadius: 999, background: dotColor }} />}
        </span>
      )}
      {icon && <Icon name={icon} size={12.5} style={{ marginRight: 6, flexShrink: 0, color: active ? 'inherit' : TOKENS.text3 }} />}
      <span className={c.label}>{label}</span>
      {suffix && <span className={c.suffix}>{suffix}</span>}
      {badge != null && <span className={c.badge}>{badge}</span>}
    </button>
  )
}

const TournamentTree = ({
  competitions = [],
  competitionDetail,
  stages = [],
  selection = {},
  onSelectCompetition,
  onSelectSeason,
  onSelectStage,
  onSelectGroup,
  onSelectGameday,
  onNewCompetition,
  onHome,
}) => {
  const c = useStyles()
  const [expanded, setExpanded] = useState({})
  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }))
  // Selecting a node (label click) navigates to it AND reveals its children, so a
  // single click never leaves the operator with a node that's active but collapsed.
  const open = (key) => setExpanded((p) => ({ ...p, [key]: true }))

  // Auto-expand the active path as the user drills in.
  useEffect(() => {
    setExpanded((p) => ({
      ...p,
      ...(selection.competitionId ? { [`c:${selection.competitionId}`]: true } : {}),
      ...(selection.seasonId ? { [`s:${selection.seasonId}`]: true } : {}),
      ...(selection.stageId ? { [`st:${selection.stageId}`]: true } : {}),
      ...(selection.groupId ? { [`g:${selection.groupId}`]: true } : {}),
    }))
  }, [selection.competitionId, selection.seasonId, selection.stageId, selection.groupId])

  const seasonsFor = (compId) =>
    competitionDetail && competitionDetail.id === compId ? competitionDetail.seasons || [] : []

  return (
    <aside className={c.rail}>
      <div className={c.head}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: TOKENS.text, whiteSpace: 'nowrap' }}>Tournament Tree</div>
        </div>
      </div>

      <div className={c.body}>
        {/* Home row — back to the all-competitions grid. Active when nothing is selected. */}
        <button
          type="button"
          className={`${c.node} ${!selection.competitionId ? c.active : ''}`}
          style={{ paddingLeft: 8 }}
          onClick={onHome}
        >
          <span className={c.twisty} />
          <Icon name="trophy" size={12.5} style={{ marginRight: 6, flexShrink: 0, color: !selection.competitionId ? 'inherit' : TOKENS.text3 }} />
          <span className={c.label}>All Competitions</span>
          <span className={c.badge}>{competitions.length}</span>
        </button>
        <div style={{ height: 1, background: TOKENS.border, margin: '6px 4px' }} />

        {competitions.map((comp) => {
          const compKey = `c:${comp.id}`
          const compOpen = expanded[compKey]
          const seasons = seasonsFor(comp.id)
          const compActive = selection.competitionId === comp.id && !selection.seasonId
          return (
            <div key={comp.id}>
              <TreeNode
                level={0}
                icon="trophy"
                label={comp.name}
                hasChildren={selection.competitionId === comp.id ? seasons.length > 0 : true}
                expanded={compOpen}
                // Seasons load only for the selected competition, so expanding another
                // one selects it first (then the active-path effect expands it).
                onToggle={() => (selection.competitionId === comp.id ? toggle(compKey) : onSelectCompetition(comp))}
                active={compActive}
                onSelect={() => { onSelectCompetition(comp); open(compKey) }}
              />
              {compOpen &&
                seasons.map((season) => {
                  const seasonKey = `s:${season.id}`
                  const seasonOpen = expanded[seasonKey]
                  const seasonStages = selection.seasonId === season.id ? stages : []
                  const seasonActive = selection.seasonId === season.id && !selection.stageId
                  return (
                    <div key={season.id}>
                      <TreeNode
                        level={1}
                        icon="calendar"
                        label={season.name}
                        hasChildren={selection.seasonId === season.id ? seasonStages.length > 0 : true}
                        expanded={seasonOpen}
                        // Stages load only for the selected season, so expanding another
                        // one selects it first (then the active-path effect expands it).
                        onToggle={() => (selection.seasonId === season.id ? toggle(seasonKey) : onSelectSeason(season))}
                        active={seasonActive}
                        onSelect={() => { onSelectSeason(season); open(seasonKey) }}
                      />
                      {seasonOpen &&
                        seasonStages.map((stage) => {
                          const stageKey = `st:${stage.id}`
                          const stageOpen = expanded[stageKey]
                          const groups = stage.groups || []
                          const stageActive = selection.stageId === stage.id && !selection.groupId
                          return (
                            <div key={stage.id}>
                              <TreeNode
                                level={2}
                                icon="layers"
                                label={stage.name}
                                hasChildren={groups.length > 0}
                                expanded={stageOpen}
                                onToggle={() => toggle(stageKey)}
                                active={stageActive}
                                onSelect={() => { onSelectStage(stage); open(stageKey) }}
                                badge={groups.length || null}
                              />
                              {stageOpen &&
                                groups.map((group) => {
                                  const groupKey = `g:${group.id}`
                                  const groupOpen = expanded[groupKey]
                                  const gamedays = group.gamedays || []
                                  const groupActive = selection.groupId === group.id && !selection.gamedayId
                                  return (
                                    <div key={group.id}>
                                      <TreeNode
                                        level={3}
                                        icon="grid"
                                        label={group.name}
                                        hasChildren={gamedays.length > 0}
                                        expanded={groupOpen}
                                        onToggle={() => toggle(groupKey)}
                                        active={groupActive}
                                        onSelect={() => { onSelectGroup(stage, group); open(groupKey) }}
                                        badge={gamedays.length || null}
                                      />
                                      {groupOpen &&
                                        gamedays.map((gd) => (
                                          <TreeNode
                                            key={gd.id}
                                            level={4}
                                            icon="play"
                                            label={gd.name}
                                            active={selection.gamedayId === gd.id}
                                            onSelect={() => onSelectGameday(stage, group, gd)}
                                            suffix={`${(gd.games || []).length}`}
                                          />
                                        ))}
                                    </div>
                                  )
                                })}
                            </div>
                          )
                        })}
                    </div>
                  )
                })}
            </div>
          )
        })}

        {onNewCompetition && (
          <div style={{ padding: '12px 8px' }}>
            <button type="button" className={c.newBtn} onClick={onNewCompetition}>
              <Icon name="plus" size={13} /> New Competition
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}

export default TournamentTree
